import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { formatDate } from "../utils/dateUtils";
import axios from '../utils/axiosConfig';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Button,
  TablePagination,
  Container,
  Menu,
  MenuItem as MuiMenuItem,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { windowsTableStyles } from "../styles/windowsTableStyles";
import { smallTitle } from '../styles/typography';
import { CircularProgress } from '@mui/material';
import Avatar from "@mui/material/Avatar";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import GavelIcon from "@mui/icons-material/Gavel"; // Courtroom icon
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';


const tableStyles = windowsTableStyles();
const CalendarTable = () => {
  const { t, i18n } = useTranslation(['common', 'buttons', 'forms', 'messages', 'pages']);
  const { id } = useParams(); // Assuming the case ID is passed as a URL parameter
  const dispatch = useDispatch();
  const navigate = useNavigate();
  // Use local state for case-specific appointments to avoid conflicts with global list
  const [caseAppointments, setCaseAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { files } = useSelector((state) => state.fileList);
  const { user } = useSelector((state) => state.userLoaded); // Assuming user info is stored in auth state

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [anchorEl, setAnchorEl] = useState(null);

  // Fetch appointments directly using axios to avoid Redux conflicts
  useEffect(() => {
    const fetchCaseAppointments = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/appointments/case/${id}`);
        setCaseAppointments(response.data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    if (id) {
      fetchCaseAppointments();
    }
  }, [id]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateAppointment = () => {
    navigate('/appointment/create', { state: { caseId: id } });
    handleMenuClose();
  };

  const handleCreateHearing = () => {
    navigate('/hearing/create', { state: { caseId: id } });
    handleMenuClose();
  };

  const userFiles = files.filter((file) => {
    if (user.role === "Manager") {
      return true; // Managers can see all files
    } else if (user.role === "Responsible Lawyer") {
      return file.officeMembers.some(
        (member) => member.member._id === user.relationship && member.role === "Responsible Lawyer"
      );
    } else if (user.role === "Assistant Lawyer") {
      return file.officeMembers.some(
        (member) => member.member._id === user.relationship && member.role === "Treating Lawyer"
      );
    }
    return false;
  });
  
  const [filteredAppointments, setFilteredAppointments] = useState([]);

  useEffect(() => {
    if (caseAppointments && caseAppointments.length > 0) {
      const filtered = caseAppointments.filter((appointment) =>
        id === appointment.case?._id
      );
      setFilteredAppointments(filtered);
    } else {
      setFilteredAppointments([]);
    }
  }, [caseAppointments, id]);


  const getColor = (color) => {
    switch (color) {
      case "Red":
        return "#e5202e";  // Blue for In Progress
      case "Blue":
        return "Blue";  // Gray for Not Started
      case "Green":
        return "#85bc20";  // Green for Completed
      case "Yellow":
        return "Yellow";
      default:
        return "transparent";  // Transparent if no status
    }
  };

  // const filteredHearings = hearings.filter((hearing) =>
  //   userFiles.some((file) => file._id === hearing.case?._id)
  // );

  // const combinedEvents = [...filteredAppointments, ...filteredHearings];
  const combinedEvents = [...filteredAppointments];
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', padding: '32px 24px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ backgroundColor: 'white', padding: "32px 24px " }}>
        <TableContainer>
          <Box
            sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
          >
            {t('common:no_data_available')}
          </Box>
        </TableContainer>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: 'white', padding: "32px 24px " }}>

      <TableContainer>
        {filteredAppointments.length > 0 ? (
          <Table sx={{ ...tableStyles.container, border: "1px solid #d1d1d1" }}
            size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.event')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.start_date')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.start_time')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.end_date')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.end_time')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.type')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.owner')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.subject')}</TableCell>
                <TableCell sx={tableStyles.headerCell}>{t('forms:labels.location')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {combinedEvents.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((event) => (
                <TableRow key={event._id}
                  sx={{ ...tableStyles.row }}>
                  <TableCell sx={tableStyles.cell}>{event.calendarType === "Appointment" ? t('pages:appointments.appointment') : t('pages:hearings.hearing')}</TableCell>
                  <TableCell sx={tableStyles.cell}>{formatDate(event.startDate)}</TableCell>
                  <TableCell sx={tableStyles.cell}>{new Date(event.startDate).toLocaleTimeString(i18n.language === 'nl' ? 'nl-NL' : 'en-US')}</TableCell>
                  <TableCell sx={tableStyles.cell}>{formatDate(event.endDate)}</TableCell>
                  <TableCell sx={tableStyles.cell}>{new Date(event.endDate).toLocaleTimeString(i18n.language === 'nl' ? 'nl-NL' : 'en-US')}</TableCell>
                  <TableCell 
                    sx={{ 
                      ...tableStyles.cell, 
                      "&:hover": { textDecoration: "underline", cursor: "pointer" }, 
                      color: "#005b92" 
                    }}
                    onClick={() => navigate(
                      event.calendarType === "Appointment" ? `/appointment/${event._id}` : `/hearing/${event._id}`,
                      { state: { caseId: id } }
                    )}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      {event.calendarType === "Appointment" && <CalendarTodayOutlinedIcon sx={{ fontSize: 13 }} />}
                      {event.calendarType === "Hearing" && <GavelIcon sx={{ fontSize: 13 }} />}
                      <span>{event.type}</span>
                    </Box>
                  </TableCell>
                  <TableCell
                    sx={{
                      ...tableStyles.cell,
                      cursor: "pointer", // Make the cell clickable
                    }}
                    onClick={() => {
                      // Navigate to the owner's profile
                      if (event.owner?._id) {
                        navigate(`/relationships/${event.owner._id}`);
                      }
                    }}
                  >
                    {event.owner ? (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        {/* Avatar */}
                        <Avatar
                          alt={event.owner.firstName}
                          src={event.owner.avatar ? event.owner.avatar : "/static/images/avatar/1.jpg"} // Fallback avatar
                          sx={{
                            width: "16px", // Avatar size
                            height: "16px", // Avatar size
                            fontSize: "7.8px", // Font size for initials
                            fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                            color: "#fff", // Text color for initials
                           
                          }}
                        >
                          {!event.owner.avatar && (
                            <>
                              {event.owner.firstName.charAt(0)}
                              {event.owner.lastName.charAt(0)}
                            </>
                          )}
                        </Avatar>

                        {/* Name */}
                        <Box
                          sx={{
                            display: "block",
                            fontFamily: '"Fira Sans", "Helvetica Neue", Helvetica, Roboto, Arial, sans-serif',
                            fontSize: "10.5px", // Font size for the name
                            fontWeight: 400,
                            lineHeight: "13.5px",
                            color: "#005b92", // Text color
                          }}
                        >
                          {`${event.owner.firstName} ${event.owner.middleName ? event.owner.middleName + " " : ""
                            }${event.owner.lastName}`}
                        </Box>
                      </Box>
                    ) : (
                      t('common:fallbacks.notAvailable') // Fallback if no owner is found
                    )}
                  </TableCell>
                  <TableCell
                    onClick={() => navigate(
                      event.calendarType === "Appointment" ? `/appointment/${event._id}` : `/hearing/${event._id}`,
                      { state: { caseId: id } }
                    )}

                    sx={{
                      ...tableStyles.cell, cursor: "pointer",
                      "&:hover": {
                        textDecoration: "underline !important",
                        cursor: "pointer",
                      },
                      color: "#005b92",
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Box
                        sx={{
                          height: "10px",
                          width: "10px",
                          backgroundColor: getColor(event?.colour),

                        }}
                      />
                      <span>{event.subject}</span>
                    </Box>
                  </TableCell>
                  <TableCell sx={tableStyles.cell}>{event.location}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Box
            sx={{ ...smallTitle, padding: "32px 24px", textAlign: "center" }}
          >
            {t('common:no_data_available')}
          </Box>
        )}
      </TableContainer>
      {filteredAppointments.length > 0 && (
        <TablePagination
          component="div"
          count={combinedEvents.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

export default CalendarTable;